import React, { useState, useEffect, useCallback, useContext, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import dayjs, { Dayjs } from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import {
  Table,
  Spin,
  Tooltip,
  Button,
  Tag,
  Space,
  Typography,
  Select,
  Progress,
  DatePicker,
} from "antd";
import {
  LoadingOutlined,
  CloudDownloadOutlined,
  DownloadOutlined,
  ShopOutlined,
  CloseCircleOutlined,
  CalendarOutlined,
} from "@ant-design/icons";
import { ColumnsType } from "antd/es/table";
import {
  PageLayout,
  PageHeader,
  SectionCard,
  TableToolbar,
  FilterCriteria,
  ToolbarAction,
} from "../../Common/Layout";
import { adminContext } from "../../Common/PageRoute";
import { FileDownloadStatus } from "../../Utilities/ApiUtility.axios";
import InvoiceExtractionService, {
  IInvoiceAdminListItem,
  IInvoiceAdminItemKey,
} from "../../Services/InvoiceExtractionService";
import { IIdNameOption } from "../../Services/XcelifyExtractionService";
import { usePractice, IPractices } from "../../Context/PracticeContext";

dayjs.extend(customParseFormat);

const { Text } = Typography;
const { RangePicker } = DatePicker;


export interface IExtractionProgressState {
  active: boolean;
  total: number;
  processed: number;
  percent: number;
}

interface ISavedJobSession {
  jobId: string;
  totalCount: number;
  startedAt: number;
}

const LOCAL_STORAGE_JOB_KEY = "invoice_active_extraction_job";

const STATUS_OPTIONS = [
  { value: "Draft", label: "Draft" },
  { value: "Sent", label: "Sent" },
  { value: "Paid", label: "Paid" },
  { value: "Overdue", label: "Overdue" },
  { value: "Cancelled", label: "Cancelled" },
];

const loadSavedJobSession = (): ISavedJobSession | null => {
  try {
    const raw = localStorage.getItem(LOCAL_STORAGE_JOB_KEY);
    if (!raw) return null;
    const data: ISavedJobSession = JSON.parse(raw);
    if (!data?.jobId || Date.now() - data.startedAt > 30 * 60 * 1000) {
      localStorage.removeItem(LOCAL_STORAGE_JOB_KEY);
      return null;
    }
    return data;
  } catch {
    localStorage.removeItem(LOCAL_STORAGE_JOB_KEY);
    return null;
  }
};

const saveJobSession = (jobId: string, totalCount: number) => {
  try {
    const session: ISavedJobSession = { jobId, totalCount, startedAt: Date.now() };
    localStorage.setItem(LOCAL_STORAGE_JOB_KEY, JSON.stringify(session));
  } catch {}
};

const clearSavedJobSession = () => {
  try {
    localStorage.removeItem(LOCAL_STORAGE_JOB_KEY);
  } catch {}
};


interface ExtractionProgressHeaderProps {
  progress: IExtractionProgressState;
  onCancel: () => void;
}

const ExtractionProgressHeader: React.FC<ExtractionProgressHeaderProps> = React.memo(
  ({ progress, onCancel }) => {
    if (!progress.active) {
      return <>Extract single or multiple invoice PDFs</>;
    }

    return (
      <div style={{ maxWidth: 480, marginTop: 4 }}>
        <Space
          style={{
            marginBottom: 2,
            width: "100%",
            justifyContent: "space-between",
          }}
        >
          <Space>
            <Text strong style={{ fontSize: 13, color: "#1890ff" }}>
              Bulk Extraction Progress: {progress.percent}%
            </Text>
            <Text type="secondary" style={{ fontSize: 12 }}>
              ({progress.processed} / {progress.total} files processed)
            </Text>
          </Space>
          <Button
            type="link"
            danger
            size="small"
            icon={<CloseCircleOutlined />}
            onClick={onCancel}
            style={{ padding: 0 }}
          >
            Cancel
          </Button>
        </Space>
        <Progress
          percent={progress.percent}
          status="active"
          strokeColor={{ "0%": "#108ee9", "100%": "#52c41a" }}
          size="small"
        />
      </div>
    );
  }
);

export interface InvoiceExtractionProps {
  onSwitch?: (type: "xcelify" | "invoice") => void;
}


export const InvoiceExtraction: React.FC<InvoiceExtractionProps> = ({ onSwitch }) => {
  const navigate = useNavigate();

  const { openNotification } = useContext(adminContext);
  const { allPracticeIds } = usePractice();

  const practiceMap = useMemo(() => {
    const map: Record<number, string> = {};
    allPracticeIds?.forEach((p: any) => {
      map[p.value] = p.label;
    });
    return map;
  }, [allPracticeIds]);

  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [activeJobId, setActiveJobId] = useState<string | null>(null);

  const [companyList, setCompanyList] = useState<IIdNameOption[]>([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);

  const [selectedPracticeId, setSelectedPracticeId] = useState<number | undefined>(undefined);
  const [selectedClientId, setSelectedClientId] = useState<string | undefined>(undefined);
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);
  const [fromDate, setFromDate] = useState<Dayjs | null>(dayjs().startOf("month"));
  const [toDate, setToDate] = useState<Dayjs | null>(dayjs().endOf("day"));

  const [items, setItems] = useState<IInvoiceAdminListItem[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(15);
  const [searchQuery, setSearchQuery] = useState("");
  const [refreshToggle, setRefreshToggle] = useState(false);

  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectedItems, setSelectedItems] = useState<IInvoiceAdminItemKey[]>([]);

  const [extractionProgress, setExtractionProgress] = useState<IExtractionProgressState>({
    active: false,
    total: 0,
    processed: 0,
    percent: 0,
  });

  const pollTimerRef = useRef<NodeJS.Timeout | null>(null);
  const resetTimerRef = useRef<NodeJS.Timeout | null>(null);
  const downloadAbortControllerRef = useRef<AbortController | null>(null);
  const isCancelledRef = useRef<boolean>(false);

  const stopPolling = useCallback(() => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  const resetProgressUI = useCallback(() => {
    stopPolling();
    setDownloading(false);
    setActiveJobId(null);
    clearSavedJobSession();
    if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
    resetTimerRef.current = setTimeout(() => {
      setExtractionProgress({ active: false, total: 0, processed: 0, percent: 0 });
      resetTimerRef.current = null;
    }, 3000);
  }, [stopPolling]);

  useEffect(() => {
    return () => {
      stopPolling();
      if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
    };
  }, [stopPolling]);

  useEffect(() => {
    const saved = loadSavedJobSession();
    if (!saved) return;

    setDownloading(true);
    setActiveJobId(saved.jobId);
    setExtractionProgress({ active: true, total: saved.totalCount, processed: 0, percent: 0 });

    stopPolling();
    pollTimerRef.current = setInterval(async () => {
      try {
        const prog = await InvoiceExtractionService.getProgress(saved.jobId);
        if (prog && prog.status !== "Not Found") {
          setExtractionProgress((prev) => ({
            active: true,
            total: prog.total || saved.totalCount || prev.total,
            processed: prog.processed ?? prev.processed,
            percent: prog.percent ?? prev.percent,
          }));

          const isTerminal =
            (prog.status === "Completed" && prog.processed >= prog.total && prog.total > 0) ||
            prog.status === "Failed" ||
            prog.status === "Cancelled";

          if (isTerminal) {
            resetProgressUI();
          }
        }
      } catch {}
    }, 1000);

    return () => stopPolling();
  }, [stopPolling, resetProgressUI]);

  // Auto-select first practice when practices load
  useEffect(() => {
    if (allPracticeIds?.length > 0 && selectedPracticeId === undefined) {
      setSelectedPracticeId(allPracticeIds[0].value);
    }
  }, [allPracticeIds, selectedPracticeId]);

  // Load Companies when Practice changes
  useEffect(() => {
    const pId = selectedPracticeId;
    if (!pId || pId <= 0) {
      setCompanyList([]);
      setSelectedClientId(undefined);
      return;
    }

    let isMounted = true;
    setLoadingCompanies(true);
    InvoiceExtractionService.getCompaniesByPractice(pId)
      .then((res) => {
        if (isMounted) setCompanyList(res || []);
      })
      .catch((err) => {
        console.error("Error loading practice companies:", err);
        if (isMounted) setCompanyList([]);
      })
      .finally(() => {
        if (isMounted) setLoadingCompanies(false);
      });

    return () => {
      isMounted = false;
    };
  }, [selectedPracticeId]);

  // Fetch Invoice records
  const fetchRecords = useCallback(async () => {
    const pId = selectedPracticeId ?? (allPracticeIds?.length > 0 ? allPracticeIds[0].value : undefined);
    if (!pId) {
      setItems([]);
      setTotalItems(0);
      return;
    }

    setLoading(true);
    try {
      const start = (currentPage - 1) * pageSize;
      const fromStr = fromDate ? fromDate.format("DD/MM/YYYY") : undefined;
      const toStr = toDate ? toDate.format("DD/MM/YYYY") : undefined;

      const res = await InvoiceExtractionService.getList(
        start,
        pageSize,
        searchQuery,
        pId,
        fromStr,
        toStr,
        selectedClientId,
        statusFilter
      );

      if (res) {
        setItems(res.items || []);
        setTotalItems(res.totalRecords || 0);
      } else {
        openNotification("error", "Failed to fetch invoice records");
      }
    } catch (err: any) {
      openNotification("error", err?.message || "Error fetching invoice records");
    } finally {
      setLoading(false);
    }
  }, [
    currentPage,
    pageSize,
    searchQuery,
    selectedPracticeId,
    allPracticeIds,
    fromDate,
    toDate,
    selectedClientId,
    statusFilter,
    openNotification,
  ]);

  useEffect(() => {
    fetchRecords();
  }, [fetchRecords, refreshToggle]);

  const resetSelectionAndPage = useCallback(() => {
    setSelectedRowKeys([]);
    setSelectedItems([]);
    setCurrentPage(1);
  }, []);

  const handlePracticeChange = useCallback((value?: number) => {
    setSelectedPracticeId(value);
    setSelectedClientId(undefined);
    setStatusFilter(undefined);
    resetSelectionAndPage();
  }, [resetSelectionAndPage]);

  const handleCompanyChange = useCallback((value?: string) => {
    setSelectedClientId(value);
    resetSelectionAndPage();
  }, [resetSelectionAndPage]);

  const handleStatusChange = useCallback((status?: string) => {
    setStatusFilter(status);
    resetSelectionAndPage();
  }, [resetSelectionAndPage]);

  const handleDateRangeChange = useCallback((from: Dayjs | null, to: Dayjs | null) => {
    setFromDate(from);
    setToDate(to);
    resetSelectionAndPage();
  }, [resetSelectionAndPage]);

  const handleSearch = useCallback((val: string) => {
    setSearchQuery(val);
    resetSelectionAndPage();
  }, [resetSelectionAndPage]);

  const handleRefresh = useCallback(() => {
    setSelectedRowKeys([]);
    setSelectedItems([]);
    setRefreshToggle((t) => !t);
  }, []);

  const handleRowSelectionChange = useCallback(
    (keys: React.Key[], selectedRows: IInvoiceAdminListItem[]) => {
      setSelectedRowKeys(keys);
      setSelectedItems(
        selectedRows.map((r) => ({ id: r.id, practiceId: r.practiceId }))
      );
    },
    []
  );

  const handleSingleDownload = useCallback(
    async (record: IInvoiceAdminListItem) => {
      setDownloadingId(record.id);
      try {
        openNotification("info", `Downloading invoice ${record.number}...`);
        const res = await InvoiceExtractionService.downloadSingle(
          record.id,
          record.practiceId
        );
        if (res?.status === FileDownloadStatus.error) {
          openNotification(
            "error",
            "Download failed. File not found or inaccessible."
          );
        }
      } catch (err: any) {
        openNotification("error", err?.message || "Failed to download single file.");
      } finally {
        setDownloadingId(null);
      }
    },
    [openNotification]
  );

  const handleCancelExtraction = useCallback(async () => {
    isCancelledRef.current = true;
    stopPolling();

    if (downloadAbortControllerRef.current) {
      downloadAbortControllerRef.current.abort();
      downloadAbortControllerRef.current = null;
    }

    const jobIdToCancel = activeJobId || loadSavedJobSession()?.jobId;

    clearSavedJobSession();
    setDownloading(false);
    setActiveJobId(null);
    setExtractionProgress({ active: false, total: 0, processed: 0, percent: 0 });

    if (jobIdToCancel) {
      try {
        await InvoiceExtractionService.cancelProgress(jobIdToCancel);
      } catch (e) {
        console.error("Cancel API error:", e);
      }
    }

    openNotification("warning", "Extraction job cancelled by user.");
  }, [activeJobId, stopPolling, openNotification]);

  const handleBulkExtraction = useCallback(
    async (extractAll: boolean = false) => {
      if (!extractAll && selectedItems.length === 0) {
        openNotification("warning", "Please select at least one record to extract.");
        return;
      }

      const count = extractAll ? totalItems : selectedItems.length;
      const jobId = `job_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`;

      const abortController = new AbortController();
      downloadAbortControllerRef.current = abortController;
      isCancelledRef.current = false;

      saveJobSession(jobId, count);
      setDownloading(true);
      setActiveJobId(jobId);
      setExtractionProgress({ active: true, total: count, processed: 0, percent: 0 });

      stopPolling();
      pollTimerRef.current = setInterval(async () => {
        try {
          const prog = await InvoiceExtractionService.getProgress(jobId);
          if (prog) {
            setExtractionProgress((prev) => ({
              active: true,
              total: prog.total || count || prev.total,
              processed: prog.processed ?? prev.processed,
              percent: prog.percent ?? prev.percent,
            }));

            const isTerminal =
              (prog.status === "Completed" &&
                prog.processed >= prog.total &&
                prog.total > 0) ||
              prog.status === "Failed" ||
              prog.status === "Cancelled";

            if (isTerminal) {
              stopPolling();
            }
          }
        } catch {}
      }, 1000);

      try {
        openNotification("info", `Streaming ${count} invoice file(s) into zip package...`);
        const pId = selectedPracticeId || 0;
        const fromIso = fromDate
          ? fromDate.startOf("day").format("YYYY-MM-DDTHH:mm:ss")
          : undefined;
        const toIso = toDate
          ? toDate.endOf("day").format("YYYY-MM-DDTHH:mm:ss")
          : undefined;

        const res = await InvoiceExtractionService.extractBulk(
          extractAll ? [] : selectedItems,
          extractAll,
          pId,
          fromIso,
          toIso,
          selectedClientId,
          statusFilter,
          searchQuery,
          jobId,
          abortController.signal
        );

        if (isCancelledRef.current) return;

        if (res?.status === FileDownloadStatus.error) {
          if (!abortController.signal.aborted && !isCancelledRef.current) {
            openNotification("error", "Extraction failed. Files could not be retrieved.");
          }
        }
      } catch (err: any) {
        if (!isCancelledRef.current && err?.name !== "AbortError" && err?.code !== "ERR_CANCELED") {
          openNotification("error", err?.message || "Failed to extract bulk files.");
        }
      } finally {
        downloadAbortControllerRef.current = null;
        if (!isCancelledRef.current) {
          resetProgressUI();
        }
      }
    },
    [
      selectedItems,
      totalItems,
      selectedPracticeId,
      fromDate,
      toDate,
      selectedClientId,
      statusFilter,
      searchQuery,
      stopPolling,
      resetProgressUI,
      openNotification,
    ]
  );

  const columns: ColumnsType<IInvoiceAdminListItem> = useMemo(
    () => [
      {
        title: "S.No.",
        dataIndex: "sno",
        key: "sno",
        width: 60,
        render: (val: number) => <span>{val}</span>,
      },
      {
        title: "Ref Number",
        dataIndex: "number",
        key: "number",
        width: 130,
        render: (text: string) => (
          <Tag color="blue" style={{ fontWeight: 600 }}>
            {text || "N/A"}
          </Tag>
        ),
      },
      {
        title: "Company",
        dataIndex: "company",
        key: "company",
        width: "22%",
        render: (comp?: { id: string; name: string }) => (
          <Space>
            <ShopOutlined style={{ color: "#1890ff" }} />
            <Text strong>{comp?.name || "N/A"}</Text>
          </Space>
        ),
      },
      {
        title: "Date",
        dataIndex: "date",
        key: "date",
        width: 110,
        render: (val: string) => (
          <Space>
            <CalendarOutlined style={{ color: "#8c8c8c" }} />
            <span>{val ? dayjs(val).format("DD/MM/YYYY") : "—"}</span>
          </Space>
        ),
      },
      {
        title: "Gross",
        dataIndex: "gross",
        key: "gross",
        width: 100,
        render: (val: number) => (
          <span>£{val != null ? Number(val).toFixed(2) : "0.00"}</span>
        ),
      },
      {
        title: "Due",
        dataIndex: "due",
        key: "due",
        width: 100,
        render: (val: number) => (
          <span
            style={{
              color: val > 0 ? "#ff4d4f" : undefined,
              fontWeight: val > 0 ? 600 : 400,
            }}
          >
            £{val != null ? Number(val).toFixed(2) : "0.00"}
          </span>
        ),
      },
      {
        title: "Status",
        dataIndex: "status",
        key: "status",
        width: 100,
        render: (val: string) => {
          const raw = val || "Draft";
          const s = raw.toLowerCase();
          const isCancelled = s === "void" || s === "cancelled";
          const displayLabel = isCancelled ? "Cancelled" : raw;
          const color =
            s === "paid"
              ? "success"
              : s === "sent"
              ? "processing"
              : s === "overdue"
              ? "error"
              : isCancelled
              ? "default"
              : "default";
          return <Tag color={color}>{displayLabel}</Tag>;
        },
      },
      {
        title: "Practice",
        dataIndex: "practiceId",
        key: "practiceId",
        width: "16%",
        render: (pId: number) => (
          <span style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <strong>{practiceMap[pId] || `Practice ${pId}`}</strong>
            <Tag color="purple" style={{ fontSize: 11 }}>
              ID: {pId}
            </Tag>
          </span>
        ),
      },
      {
        title: "Action",
        key: "action",
        width: 70,
        render: (_, record: IInvoiceAdminListItem) => {
          const isThisDownloading = downloadingId === record.id;
          return (
            <Tooltip
              title={
                isThisDownloading
                  ? "Downloading invoice..."
                  : "Download Invoice PDF"
              }
            >
              <Button
                type="text"
                loading={isThisDownloading}
                icon={
                  !isThisDownloading ? (
                    <DownloadOutlined style={{ color: "#1890ff", fontSize: 16 }} />
                  ) : undefined
                }
                onClick={() => handleSingleDownload(record)}
              />
            </Tooltip>
          );
        },
      },
    ],
    [practiceMap, downloadingId, handleSingleDownload]
  );

  const primaryActions: ToolbarAction[] = useMemo(
    () => [
      {
        key: "extractSelected",
        label: `Selected ${
          selectedRowKeys.length > 0 ? `(${selectedRowKeys.length})` : ""
        }`,
        icon: <CloudDownloadOutlined />,
        type: "primary",
        disabled: selectedRowKeys.length === 0,
        loading: downloading,
        onClick: () => handleBulkExtraction(false),
      },
      {
        key: "extractAll",
        label: `Extract All (${totalItems})`,
        icon: <CloudDownloadOutlined />,
        type: "default",
        disabled: totalItems === 0,
        loading: downloading,
        onClick: () => handleBulkExtraction(true),
      },
    ],
    [selectedRowKeys.length, totalItems, downloading, handleBulkExtraction]
  );

  const rowSelection = useMemo(
    () => ({
      selectedRowKeys,
      onChange: handleRowSelectionChange,
    }),
    [selectedRowKeys, handleRowSelectionChange]
  );

  const datePresets = useMemo(() => {
    const now = dayjs();
    const quarterStartMonth = Math.floor(now.month() / 3) * 3;
    const previousQuarterYear = now.month() < 3 ? now.year() - 1 : now.year();
    const previousQuarterStartMonth = now.month() < 3 ? 9 : quarterStartMonth - 3;

    return [
      {
        label: "This Month",
        value: [dayjs().startOf("month"), dayjs().endOf("day")] as [Dayjs, Dayjs],
      },
      {
        label: "Last Month",
        value: [
          dayjs().subtract(1, "month").startOf("month"),
          dayjs().subtract(1, "month").endOf("month"),
        ] as [Dayjs, Dayjs],
      },
      {
        label: "This Quarter",
        value: [
          now.startOf("year").month(quarterStartMonth).startOf("month"),
          now.endOf("day"),
        ] as [Dayjs, Dayjs],
      },
      {
        label: "Last Quarter",
        value: [
          dayjs()
            .year(previousQuarterYear)
            .startOf("year")
            .month(previousQuarterStartMonth)
            .startOf("month"),
          dayjs()
            .year(previousQuarterYear)
            .startOf("year")
            .month(previousQuarterStartMonth + 2)
            .endOf("month"),
        ] as [Dayjs, Dayjs],
      },
      {
        label: "This Year",
        value: [dayjs().startOf("year"), dayjs().endOf("day")] as [Dayjs, Dayjs],
      },
      {
        label: "Last Year",
        value: [
          dayjs().subtract(1, "year").startOf("year"),
          dayjs().subtract(1, "year").endOf("year"),
        ] as [Dayjs, Dayjs],
      },
    ];
  }, []);

  const companyOptions = useMemo(
    () => companyList.map((c) => ({ value: c.id, label: c.name })),
    [companyList]
  );

  const practiceOptions = useMemo(
    () =>
      (allPracticeIds || []).map((p: IPractices) => ({
        value: p.value,
        label: `${p.label} (ID: ${p.value})`,
      })),
    [allPracticeIds]
  );

  const filterCriteria: FilterCriteria[] = useMemo(
    () => [
      {
        key: "clientId",
        label: "Company",
        type: "select" as const,
        placeholder: !selectedPracticeId
          ? "Select practice first"
          : loadingCompanies
          ? "Loading..."
          : "Select Company",
        options: companyOptions,
      },
      {
        key: "status",
        label: "Status",
        type: "select" as const,
        placeholder: "Select Status",
        options: STATUS_OPTIONS,
      },
    ],
    [selectedPracticeId, loadingCompanies, companyOptions]
  );

  const activeFilters = useMemo(
    () => ({
      clientId: selectedClientId,
      status: statusFilter,
    }),
    [selectedClientId, statusFilter]
  );

  return (
    <PageLayout>
      <PageHeader
        title="Invoice Extraction"
        subtitle={
          <ExtractionProgressHeader
            progress={extractionProgress}
            onCancel={handleCancelExtraction}
          />
        }
        actions={[
          <Select
            key="module-switcher"
            value="invoice"
            onChange={(val) => {
              if (val === "xcelify") {
                if (onSwitch) onSwitch("xcelify");
                else navigate("/xcelify-extraction");
              }
            }}
            options={[
              { value: "xcelify", label: "Xcelify Extraction" },
              { value: "invoice", label: "Invoice Extraction" },
            ]}
            style={{ width: 180 }}
          />,
        ]}
      />
      <SectionCard noPadding>
        <div style={{ padding: "0 16px" }}>
          <TableToolbar
            primaryActions={primaryActions}
            onSearch={handleSearch}
            searchPlaceholder="Search Ref No, Company..."
            onRefresh={handleRefresh}
            filterCriteria={filterCriteria}
            activeFilters={activeFilters}
            onFiltersChange={(filters) => {
              handleCompanyChange(filters.clientId);
              handleStatusChange(filters.status);
            }}
            extraBeforeFilters={
              <RangePicker
                allowClear={false}
                format="DD/MM/YYYY"
                presets={datePresets}
                value={[fromDate, toDate]}
                onChange={(dates) => {
                  if (dates) {
                    handleDateRangeChange(dates[0], dates[1]);
                  } else {
                    handleDateRangeChange(null, null);
                  }
                }}
                style={{ width: 250 }}
              />
            }
            extra={
              <Select
                showSearch
                allowClear
                placeholder="Select Practice"
                style={{ width: 200 }}
                value={selectedPracticeId}
                onChange={handlePracticeChange}
                optionFilterProp="label"
                filterOption={(input, option: any) =>
                  (option?.label ?? "")
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                options={practiceOptions}
              />
            }
          />
        </div>
        <Table
          dataSource={items}
          columns={columns}
          rowSelection={rowSelection}
          rowKey="id"
          size="small"
          scroll={{ x: 1000, y: "calc(100vh - 270px)" }}
          loading={{
            spinning: loading || downloading,
            indicator: <Spin indicator={<LoadingOutlined />} />,
          }}
          className="border-top-table"
          pagination={{
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} records`,
            current: currentPage,
            pageSize: pageSize,
            showSizeChanger: true,
            total: totalItems,
            pageSizeOptions: ["15", "25", "50", "100"],
          }}
          onChange={(paging) => {
            if (paging?.current !== undefined && paging?.pageSize !== undefined) {
              setCurrentPage(paging.current);
              setPageSize(paging.pageSize);
            }
          }}
        />
      </SectionCard>
    </PageLayout>
  );
};

export default InvoiceExtraction;
